import {
	DatePicker,
	FormItem,
	Toggle,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import { TimeZone } from "client/DateAndTime/timezones";
import styles from "./DateSelect.module.less";
import { isNil, isNotNil } from "client/Misc/utils";
import {
	addTime,
	areOnTheSameDay,
	calculateDayDifference,
	getStartOfDay,
	isValidDate,
	millisecondsInAMinute
} from "client/DateAndTime/dateTimeUtils";
import {
	getAppointmentDayOfWeek,
	RecurrencePatternKindSelector
} from "./CalendarSidebarContent/RepeatOptionSelector/RecurrencePatternKindSelector";
import {
	RecurrencePatternKind,
	CalendarAppointmentFormModel,
	RecurrenceOptionsFormModel,
	CustomRecurrencePatternKind
} from "./common";
import { RepeatEndDateSelector } from "./CalendarSidebarContent/RepeatEndDateSelector";
import { CustomRecurrenceOptions } from "./CalendarSidebarContent/RepeatOptionSelector/CustomRecurrenceOptions";
import { IValueInputAttachable } from "client/Forms/attachable";
import { AppointmentDayOfWeek } from "client/Api/Calendar/calendar";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { isEndTimeValid } from "./ComposeAppointmentDialog/ComposeAppointmentDialog";

export type DateSelectProps = {
	attachable: IValueInputAttachable<CalendarAppointmentFormModel>;
	timezone: TimeZone;
	isMobile: boolean;
	hasLabel?: boolean;
	shouldShowRecurrenceOptions?: boolean;
	isDontRepeatDisabled?: boolean;
};

export const DateSelect = (props: DateSelectProps): ReactElement => {
	const {
		attachable,
		timezone,
		isMobile,
		hasLabel = false,
		shouldShowRecurrenceOptions = false,
		isDontRepeatDisabled = false
	} = props;

	const recurrenceOptionsAttachable: IValueInputAttachable<RecurrenceOptionsFormModel> = attachable.for("recurrenceOptions");
	const lastValidEndRef: RefObject<Date | null> = useRef<Date | null>(null);

	const isAllDay: boolean = attachable.value
		.isAllDay;

	const startValue: Date | null = attachable.for("startTime")
		.value;

	const endValue: Date | null = attachable.for("endTime")
		.value;

	useEffect(
		() => {
			if (isNil(lastValidEndRef.current) && isNotNil(endValue)) {
				lastValidEndRef.current = endValue;
			}
		},
		[endValue]
	);

	const startDateChangeHandler = useCallback(
		(value: Date | null): void => {
			if (isNil(value) || !isValidDate(value)) {
				return;
			}

			if (
				isNil(startValue)
					&& isNotNil(endValue)
					&& isEndTimeValid(
						value,
						endValue,
						isAllDay,
						timezone
					)
			) {
				lastValidEndRef.current = endValue;
				return;
			}

			const newEndValue = new Date(
				value.getTime() + getEventDurationMs(startValue, endValue)
			);

			lastValidEndRef.current = newEndValue;

			attachable.for("endTime")
				.reducer
				.setValue(newEndValue);

			attachable.for("endTime")
				.validation
				.triggerInputFinished();
		},
		[
			attachable,
			startValue,
			endValue,
			isAllDay,
			timezone
		]
	);

	const endDateChangeHandler = useCallback(
		(value: Date | null): void => {
			if ((isNil(startValue) || !isValidDate(startValue)) && isValidDate(value)) {
				lastValidEndRef.current = value;
				return;
			}

			if (isEndTimeValid(
				startValue,
				value,
				isAllDay,
				timezone
			)) {
				lastValidEndRef.current = value;

				return;
			}

			let newEndValue: Date | null;

			if (
				isNotNil(startValue)
					&& isNotNil(value)
					&& isValidDate(value)
					&& areOnTheSameDay(
						value,
						startValue,
						timezone
					)
			) {
				newEndValue = value.getTime() < startValue.getTime()
					? addTime(
						startValue,
						30,
						"minute"
					)
					: value;
			} else {
				newEndValue = isNotNil(lastValidEndRef.current)
					? lastValidEndRef.current
					: value;
			}

			lastValidEndRef.current = newEndValue;

			attachable.for("endTime")
				.reducer
				.setValue(newEndValue);

			attachable.for("endTime")
				.validation
				.triggerInputFinished();
		},
		[
			attachable,
			startValue,
			isAllDay,
			timezone
		]
	);

	const startDate: Date = useMemo(
		() => attachable.for("startTime")
			.value ?? new Date(),
		[attachable]
	);

	const isAllDayChangedHandler = useCallback(
		(isAllDay: boolean): void => {
			const {
				startTime,
				endTime
			} = attachable.value;

			if (!isAllDay || isNil(startTime)) {
				return;
			}

			attachable.for("startTime")
				.reducer
				.setValue(
					getStartOfDay(startTime, timezone)
				);

			attachable.for("startTime")
				.validation
				.triggerInputFinished();

			if (isNil(endTime)) {
				return;
			}

			const dayDifference: number = calculateDayDifference(
				endTime,
				startTime,
				timezone
			);

			if (dayDifference < 0) {
				attachable.for("endTime")
					.reducer
					.setValue(startTime);

				attachable.for("endTime")
					.validation
					.triggerInputFinished();
			}

		},
		[
			attachable,
			timezone
		]
	);

	const kindChangedHandler = useCallback(
		(kind: RecurrencePatternKind): void => {
			if (kind !== RecurrencePatternKind.Custom) {
				return;
			}

			const customOptionsAttachable = attachable
				.for("recurrenceOptions")
				.for("customRecurrenceOptions");

			const appointmentDayOfWeek: AppointmentDayOfWeek = getAppointmentDayOfWeek(
				startDate,
				timezone
			);

			customOptionsAttachable.reducer
				.setValue({
					repeatInterval: 1,
					kind: CustomRecurrencePatternKind.Weekly,
					weeklyCustomRecurrenceOptions: {
						daysOfWeek: [appointmentDayOfWeek]
					},
					monthlyCustomRecurrenceOptions: {
						kind: "absolute"
					},
					yearlyCustomRecurrenceOptions: {
						kind: "absolute"
					}
				});

			customOptionsAttachable.validation
				.triggerInputFinished();
		},
		[
			attachable,
			timezone,
			startDate
		]
	);

	return (
		<>
			<FormItem
				label={hasLabel
					? localize("_StartDate")
					: undefined
				}
				isRequired
			>
				<div className={styles.dateRow}>
					<div>
						<DatePicker
							{...attachable.for("startTime")
								.attachToDatePicker()
								.withOnChange(startDateChangeHandler)
							}
							timezone={timezone}
							responsiveBehavior={isMobile ? "wrap" : "nowrap"}
							mode={isAllDay ? "date" : "datetime"}
						/>
						<ValidationError {...attachable.for("startTime").attachToValidationError()} />
					</div>
					<div>
						<NoUniqueInputIdContext>
							<Toggle
								{...attachable.for("isAllDay").attachToCheckbox()
									.withOnChange(isAllDayChangedHandler)}
								text={localize("_AllDay")}
							/>
							<ValidationError {...attachable.for("isAllDay").attachToValidationError()} />
						</NoUniqueInputIdContext>
					</div>
				</div>
			</FormItem>

			<FormItem
				label={hasLabel
					? localize("_EndDate")
					: undefined
				}
				isRequired
				hasNoMargin={!hasLabel}
			>
				<div className={styles.dateRow}>
					<div>
						<DatePicker
							{...attachable.for("endTime")
								.attachToDatePicker()
								.withOnChange(endDateChangeHandler)
							}
							timezone={timezone}
							responsiveBehavior={isMobile ? "wrap" : "nowrap"}
							mode={isAllDay ? "date" : "datetime"}
							minDate={startDate}
						/>
						<ValidationError {...attachable.for("endTime").attachToValidationError()} />
					</div>
					{shouldShowRecurrenceOptions && (
						<div>
							<RecurrencePatternKindSelector
								attachable={recurrenceOptionsAttachable.for("recurrencePatternKind")}
								timezone={timezone}
								startDate={startDate}
								isDontRepeatDisabled={isDontRepeatDisabled}
								onChange={kindChangedHandler}
							/>
							<ValidationError {...recurrenceOptionsAttachable.for("recurrencePatternKind").attachToValidationError()} />
						</div>
					)}
				</div>
			</FormItem>

			{shouldShowRecurrenceOptions && recurrenceOptionsAttachable.when(
				x => x.recurrencePatternKind !== RecurrencePatternKind.DontRepeat,
				handler => (
					<>
						{handler.value.recurrencePatternKind === RecurrencePatternKind.Custom && (
							<CustomRecurrenceOptions
								attachable={handler.for("customRecurrenceOptions")}
								startDate={startDate}
								timezone={timezone}
							/>
						)}

						<RepeatEndDateSelector
							attachable={handler.for("range")}
							timezone={timezone}
						/>
					</>
				)
			)}
		</>
	);
};
DateSelect.displayName = "DateSelect";

const DEFAULT_DURATION_MS: number = 30 * millisecondsInAMinute;

export function getEventDurationMs(start: Date | null, end: Date | null): number {
	if (isNil(start) || isNil(end)) {
		return DEFAULT_DURATION_MS;
	}

	const diff: number = end.getTime() - start.getTime();

	return diff > 0 ? diff : DEFAULT_DURATION_MS;
}